import * as Admin from '@/services/AdminDirectoryService.js';

// Superadmin management of the public directory (schools, timetables,
// dance styles, organizers, events, imports, inbox and stats).
// Lists are kept in state; detail/mutation actions return the API data.

export default {
  namespaced: true,
  state() {
    return {
      schools: [],
      genres: [],
      organizers: [],
      events: [],
    };
  },
  getters: {
    getSchools: (state) => state.schools,
    getGenres: (state) => state.genres,
    getActiveGenres: (state) => state.genres.filter((genre) => genre.active),
    getOrganizers: (state) => state.organizers,
    getEvents: (state) => state.events,
  },
  mutations: {
    setSchools(state, value) {
      state.schools = value;
    },
    setGenres(state, value) {
      state.genres = value;
    },
    setOrganizers(state, value) {
      state.organizers = value;
    },
    setEvents(state, value) {
      state.events = value;
    },
  },
  actions: {
    // ---- Schools
    async fetchSchools(context, params) {
      const response = await Admin.getSchools(params);
      context.commit('setSchools', response.data.schools);
      return response.data.schools;
    },
    async fetchSchool(_, schoolId) {
      return (await Admin.getSchool(schoolId)).data;
    },
    async saveSchool(_, { schoolId, data }) {
      const response = schoolId
        ? await Admin.updateSchool(schoolId, data)
        : await Admin.createSchool(data);
      return response.data.school;
    },
    async deleteSchool(_, schoolId) {
      await Admin.deleteSchool(schoolId);
    },
    async verifyTimetable(_, schoolId) {
      return (await Admin.verifyTimetable(schoolId)).data.timetableVerifiedAt;
    },
    async uploadSchoolImages(_, { schoolId, files }) {
      return (await Admin.uploadSchoolImages(schoolId, files)).data.school;
    },

    // ---- Weekly classes
    async saveClass(_, { schoolId, classId, data }) {
      const response = classId
        ? await Admin.updateClass(classId, data)
        : await Admin.createClass(schoolId, data);
      return response.data.class;
    },
    async deleteClass(_, classId) {
      await Admin.deleteClass(classId);
    },

    // ---- Genres
    async fetchGenres(context) {
      const response = await Admin.getGenres();
      context.commit('setGenres', response.data.genres);
      return response.data.genres;
    },
    async saveGenre(context, { genreId, data }) {
      if (genreId) await Admin.updateGenre(genreId, data);
      else await Admin.createGenre(data);
      return context.dispatch('fetchGenres');
    },
    async deleteGenre(context, genreId) {
      await Admin.deleteGenre(genreId);
      return context.dispatch('fetchGenres');
    },

    // ---- Organizers
    async fetchOrganizers(context) {
      const response = await Admin.getOrganizers();
      context.commit('setOrganizers', response.data.organizers);
      return response.data.organizers;
    },
    async saveOrganizer(context, { organizerId, data, logo }) {
      const response = organizerId
        ? await Admin.updateOrganizer(organizerId, data)
        : await Admin.createOrganizer(data);
      if (logo) await Admin.uploadOrganizerLogo(response.data.organizer._id, logo);
      return context.dispatch('fetchOrganizers');
    },
    async deleteOrganizer(context, organizerId) {
      await Admin.deleteOrganizer(organizerId);
      return context.dispatch('fetchOrganizers');
    },

    // ---- Events
    async fetchEvents(context, params) {
      const response = await Admin.getEvents(params);
      context.commit('setEvents', response.data.events);
      return response.data.events;
    },
    async saveEvent(_, { eventId, data, cover }) {
      const response = eventId
        ? await Admin.updateEvent(eventId, data)
        : await Admin.createEvent(data);
      if (cover) await Admin.uploadEventCover(response.data.event._id, cover);
      return response.data.event;
    },
    async fetchOccurrences(_, eventId) {
      return (await Admin.getEvent(eventId)).data.occurrences;
    },
    async saveOccurrence(_, { eventId, dateKey, data, cover }) {
      let response = await Admin.updateOccurrence(eventId, dateKey, data);
      if (cover) response = await Admin.uploadOccurrenceCover(eventId, dateKey, cover);
      return response.data.occurrences;
    },
    async resetOccurrence(_, { eventId, dateKey }) {
      return (await Admin.resetOccurrence(eventId, dateKey)).data.occurrences;
    },
    async deleteEvent(_, eventId) {
      await Admin.deleteEvent(eventId);
    },

    // ---- Import
    async importCsv(_, { type, file, dryRun, replace }) {
      return (await Admin.importCsv(type, file, { dryRun, replace })).data;
    },

    // ---- Inbox & stats
    async fetchReports(_, params) {
      return (await Admin.getReports(params)).data.reports;
    },
    async updateReport(_, { reportId, status }) {
      return (await Admin.updateReport(reportId, status)).data.report;
    },
    async fetchClaims() {
      return (await Admin.getClaims()).data.waitlistEntries;
    },
    async updateClaim(_, { entryId, status }) {
      return (await Admin.updateClaim(entryId, status)).data.waitlistEntry;
    },
    async fetchStats(_, days) {
      return (await Admin.getStats(days)).data;
    },
  },
};
